import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { get } from 'lodash';

import { CountryCodes } from 'src/common/enums';
import { StringUtils } from 'src/common/utils/stringUtils';
import {
  DefaultLocation,
  NewSearchLocation,
} from 'src/global/models/NewSearchLocation';
import { useSuggestedLocations } from 'src/modules/Opportunities/pages/Explore/Components/SearchSection/LocationSearchField/useSuggestedLocations';
import {
  HierarchicalLocation,
  QuerySearchHierarchicalLocationsArgs,
  searchHierarchicalLocation,
  SearchHierarchicalLocationData,
} from 'src/modules/Profile/graphql/hierarchicalLocations';

import { getFilterSpecialRegions, getSpecialRegions } from './helpers';

type UseLocationSuggestionsQueryHookResult = {
  data: NewSearchLocation[];
  suggestionsLoading: boolean;
  searchLoading: boolean;
};

type LocationSuggestionOptions = {
  selectedLocation?: NewSearchLocation;
  noDefaultLocation?: boolean;
  shouldShowL2?: boolean;
};

export const useLocationSuggestionsQuery = (
  searchTerm: string,
  suggestionLimit: number,
  selectedCountry: CountryCodes,
  options?: LocationSuggestionOptions
): UseLocationSuggestionsQueryHookResult => {
  const selectedLocName = options?.selectedLocation?.name;
  /** this will handle the case where `formattedName` used in search
   * input is different from `name` used in the location suggestion.
   * eg: "Kab. Sleman" vs "Sleman"
   */
  const searchTermForSelectedLocation =
    selectedLocName && searchTerm.includes(selectedLocName)
      ? selectedLocName
      : searchTerm;

  const suggestedLocationsResult = useSuggestedLocations(selectedCountry);

  const searchHierarchicalLevels = (
    selectedCountry: CountryCodes
  ): number[] => {
    switch (selectedCountry) {
      case CountryCodes.VN:
        return [2];
      case CountryCodes.ID:
      case CountryCodes.SG:
      case CountryCodes.TW:
      case CountryCodes.MY:
        return [2, 3];
    }
  };

  const searchHierarchicalResult = useQuery<
    SearchHierarchicalLocationData,
    QuerySearchHierarchicalLocationsArgs
  >(searchHierarchicalLocation, {
    variables: {
      searchTerm: searchTermForSelectedLocation,
      limit: suggestionLimit,
      levels: searchHierarchicalLevels(selectedCountry),
      countryCode: selectedCountry,
    },
  });

  const appendL2Locations = useCallback(
    function appendL2Locations(locations: HierarchicalLocation[]) {
      return locations
        .flatMap((result: HierarchicalLocation) => {
          const locations = [new NewSearchLocation(null, result)];
          if (!options?.shouldShowL2) {
            return locations;
          }
          const parent = result.parents?.find((parent) => parent.level === 2);
          if (
            StringUtils.ignoreCaseInclude(
              parent?.formattedName,
              searchTermForSelectedLocation
            )
          ) {
            locations.unshift(new NewSearchLocation(null, parent));
          }
          return locations;
        })
        .filter(
          (location, index, self) =>
            index === self.findIndex((t) => t.label === location.label)
        )
        .sort(
          (a, b) =>
            (a.hierarchicalLocation?.level ?? 0) -
            (b.hierarchicalLocation?.level ?? 0)
        );
    },
    [options?.shouldShowL2, searchTermForSelectedLocation]
  );

  const mappedHierarchyResults = useMemo(() => {
    const specialRegions = getSpecialRegions(selectedCountry);
    if (searchTermForSelectedLocation.length > 0) {
      const filteredSpecialRegions = getFilterSpecialRegions(
        specialRegions,
        searchTermForSelectedLocation
      );
      // TODO: restore this when searchHierarchicalLocations is migrated to V2
      const searchResults = appendL2Locations(
        get<SearchHierarchicalLocationData, 'searchHierarchicalLocations', []>(
          searchHierarchicalResult.data,
          'searchHierarchicalLocations',
          []
        )
      );
      return [...filteredSpecialRegions, ...searchResults];
    } else {
      // TODO: restore this when searchHierarchicalLocations is migrated to V2
      const suggestItems = appendL2Locations(
        suggestedLocationsResult.suggestedLocations
      );

      if (options?.noDefaultLocation) {
        return suggestItems;
      }
      return [
        DefaultLocation.getDefaultLocationAll(selectedCountry),
        ...specialRegions,
        ...suggestItems,
      ];
    }
  }, [
    appendL2Locations,
    options?.noDefaultLocation,
    searchHierarchicalResult.data,
    searchTermForSelectedLocation,
    selectedCountry,
    suggestedLocationsResult.suggestedLocations,
  ]);

  return {
    data: mappedHierarchyResults,
    suggestionsLoading: suggestedLocationsResult.isLoading,
    searchLoading: searchHierarchicalResult.loading,
  };
};
